import { ReactElement } from 'react';
import { parse, addHours, differenceInMilliseconds } from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';

import { AircraftIcon, ClinicalSpecialistIcon, SurgeonIcon, OcsDeviceIcon } from '@icons';
import {
  HubMarker,
  DonorHospitalMarker,
  TxCenterMarker,
  AircraftMarker,
  SurgeonMarker,
} from '@lp/features/RoutePlanner/components/RouteMap/markers';
import { Case, OrganType } from '@/api/apis/case';
import { HOUR_IN_MS, MINUTE_IN_MS } from '@/constants/date.constants';
import { convertToIanaTimezone, convertToTimezoneOffset } from '@/utils/helpers';
import { MapFilterType, MapMarkerType, MarkerTypeEnum } from './types';
import { SelectItemType } from '@/types';
import { RouteFilterType } from '@/types/route';
import { AIR_BLOCK_TIME } from '@/utils/route';
import {
  AoAssignmentStatus,
  CheckpointModeType,
  DriverAssignmentStatus,
  GroundTripStatus,
} from '@/api/apis/drive-service';
import { RouteTypeSettingsEnum } from '@/api/apis/mission-planner';

export const APPLY_AUTO_ROUTE_MISSING_RESOURCES_TEXT =
  'One or more resources assigned to this route are no longer available. To apply a route, select an option with available resources or regenerate route options.';
export const APPLY_AUTO_ROUTE_COMPLETED_LEGS_TEXT =
  "This route contains completed legs that can't be removed in bulk. Adjust or remove them manually before applying a new route.";
export const APPLY_AUTO_ROUTE_STATUS_TEXT = "A new route can only be applied in 'Route Planning' status.";
export const APPLY_AUTO_ROUTE_APPLIED_TEXT = 'This route has already been applied.';

export const DEVICE_REMOVAL_TEXT =
  'The Device has a planned route. Please select how to manage the legs assigned to this resource before proceeding with removal.';
export const CS_REMOVAL_TEXT =
  'The clinical specialist has a planned route. Please select how to manage the legs assigned to this resource before proceeding with removal.';
export const RESOURCE_REMOVE_TEXT = 'Remove resource and the resource specific legs ';
export const RESOURCE_SWAP_TEXT = 'Swap resource';

export const AIRCRAFT_MAX_FLIGHT_TEXT = 'Maximum allowed flight time is 4 hours (w/o block time).';
export const AIRCRAFT_REFUEL_WARNING_TEXT = `${AIRCRAFT_MAX_FLIGHT_TEXT} Consider creating the refueling stop.`;

export const EUROPE_DISABLED_MESSAGE = 'Suggested Route is not available for Europe.';
export const KIDNEY_DISABLED_MESSAGE = 'Suggested Route is not available for kidney cases.';

export const SUGGESTED_CURRY_SERVICE_LEG_MESSAGE =
  'It is assumed that the device will be transported to the HUB from the TX Center using commercial ground transportation. Please consider adding this leg manually.';
export const SUGGESTED_TEDS_LEG_MESSAGE =
  'It is assumed that the device will be transported to the HUB from the TX Center using TEDS transportation. Please consider updating the leg manually, if required.';

export const LEGACY_GROUND_APPROVE_DISABLED_MESSAGE = `The Ground Specialist shall be able to confirm ground when Driver, Vehicle Type, Plate number, Reservation Number, Phone Number OR Email are specified for each TMDX Affiliate ground leg.`;
export const TMDX_DRIVE_GROUND_APPROVE_DISABLED_MESSAGE = `The Ground Specialist shall be able to confirm ground when neither of the trips associated with TMDX Drive integrated ground legs within both Main Route and Dry Run is in ‘New’ or 'Pending' status.`;

export const MARKER_TYPE_ICONS: Partial<Record<MapFilterType, ReactElement>> = {
  [MapFilterType.aircraft]: <AircraftIcon height={16} width={16} />,
  [MapFilterType.clinicalSpecialists]: <ClinicalSpecialistIcon height={16} width={16} />,
  [MapFilterType.ocsDevices]: <OcsDeviceIcon height={16} width={16} />,
  [MapFilterType.surgeon]: <SurgeonIcon height={16} width={16} />,
};

export const renderMapMarker = (data: MapMarkerType, organType: OrganType, showTooltips: boolean = false) => {
  switch (data.type) {
    case MarkerTypeEnum.hub:
      return <HubMarker key={data.id} showTooltip={showTooltips} {...data} />;

    case MarkerTypeEnum.donorHospital:
      return <DonorHospitalMarker key={data.id} organType={organType} {...data} />;

    case MarkerTypeEnum.txCenter:
      return <TxCenterMarker key={data.id} {...data} />;

    case MarkerTypeEnum.aircraft:
      return <AircraftMarker key={data.id} {...data} />;

    case MarkerTypeEnum.surgeon:
      return <SurgeonMarker key={data.id} showTooltip={showTooltips} {...data} />;

    default:
      return null;
  }
};

export const getDefaultFilters = ({
  donorHospitalDate,
  donorHospitalTime,
  donorHospitalTimeZone,
}: Pick<Case, 'donorHospitalDate' | 'donorHospitalTime' | 'donorHospitalTimeZone'>) => {
  const tzOffset = donorHospitalTimeZone ? getTimezoneOffset(convertToIanaTimezone(donorHospitalTimeZone)) : 0;
  const tzOffsetString = donorHospitalTimeZone ? convertToTimezoneOffset(tzOffset) : 'Z';

  const calculatedOrTime = parse(
    `${donorHospitalDate} ${donorHospitalTime} ${tzOffsetString}`,
    'M/dd/yyyy HH:mm XXX',
    new Date(),
  );

  const now = new Date();
  const orTimeWithOffset = addHours(calculatedOrTime, 4);
  const isOrTimeInFuture = differenceInMilliseconds(now.getTime(), calculatedOrTime.getTime()) < 0;

  return {
    [MapFilterType.from]: now.toISOString(),
    [MapFilterType.to]: isOrTimeInFuture ? orTimeWithOffset.toISOString() : addHours(now, 4).toISOString(),
    [MapFilterType.showReserved]: false,
    [MapFilterType.aircraft]: true,
    [MapFilterType.ocsDevices]: true,
    [MapFilterType.clinicalSpecialists]: true,
    [MapFilterType.surgeon]: true,
  };
};

export const FILTER_LIST: { type: MapFilterType; hasIcon: boolean; isDisabled: boolean; title: string }[] = [
  {
    type: MapFilterType.showReserved,
    hasIcon: false,
    isDisabled: false,
    title: 'Show reserved',
  },
  {
    type: MapFilterType.aircraft,
    hasIcon: true,
    isDisabled: false,
    title: 'Aircrafts',
  },
  {
    type: MapFilterType.ocsDevices,
    hasIcon: true,
    isDisabled: false,
    title: 'OCS Devices',
  },
  {
    type: MapFilterType.clinicalSpecialists,
    hasIcon: true,
    isDisabled: false,
    title: 'Clinical Specialists',
  },
  {
    type: MapFilterType.surgeon,
    hasIcon: true,
    isDisabled: false,
    title: 'Surgeon',
  },
];

export const SWITCH_DURATION = 15 * MINUTE_IN_MS;
export const SWITCH_DURATION_LOWER = 10 * MINUTE_IN_MS;
export const REFUEL_DURATION = HOUR_IN_MS;
export const MAX_BLOCKED_LEG_FLIGHT_HOURS = 4 * HOUR_IN_MS + AIR_BLOCK_TIME;
export const MAX_LEG_FLIGHT_HOURS = 3.5 * HOUR_IN_MS + AIR_BLOCK_TIME;
export const MAX_LEG_DRIVE_DURATION = 3 * HOUR_IN_MS;
export const MI_TO_KM = 1.60934;

export const OPERATION_AT_TX_CENTER_DURATION = HOUR_IN_MS;

export const SEGMENT_END_TIME_OFFSET = HOUR_IN_MS;

export const ROUTE_FILTER_TYPE_TITLE_MAP: Record<RouteFilterType, string> = {
  [RouteFilterType.Full]: 'Full route (edit)',
  [RouteFilterType.CS]: 'Clinical Specialist’s route (view)',
  [RouteFilterType.Surgeon]: 'Retrieval Surgeon’s route (view)',
};

export const CASE_LOGISTICS_ROUTE_FILTER_TYPE_TITLE_MAP: Record<RouteFilterType, string> = {
  [RouteFilterType.Full]: 'Full route',
  [RouteFilterType.CS]: 'Clinical Specialist’s route',
  [RouteFilterType.Surgeon]: 'Retrieval Surgeon’s route',
};

export const CASE_LOGISTICS_ROUTE_FILTER_TYPE_LIST: SelectItemType[] = [
  {
    value: RouteFilterType.Full,
    name: CASE_LOGISTICS_ROUTE_FILTER_TYPE_TITLE_MAP[RouteFilterType.Full],
  },
  {
    value: RouteFilterType.CS,
    name: CASE_LOGISTICS_ROUTE_FILTER_TYPE_TITLE_MAP[RouteFilterType.CS],
  },
  {
    value: RouteFilterType.Surgeon,
    name: CASE_LOGISTICS_ROUTE_FILTER_TYPE_TITLE_MAP[RouteFilterType.Surgeon],
  },
];

export const ROUTE_FILTER_TYPE_LIST: SelectItemType[] = [
  {
    value: RouteFilterType.Full,
    name: ROUTE_FILTER_TYPE_TITLE_MAP[RouteFilterType.Full],
  },
  {
    value: RouteFilterType.CS,
    name: ROUTE_FILTER_TYPE_TITLE_MAP[RouteFilterType.CS],
  },
  {
    value: RouteFilterType.Surgeon,
    name: ROUTE_FILTER_TYPE_TITLE_MAP[RouteFilterType.Surgeon],
  },
];

export const GROUND_TRIP_CHECKPOINT_TYPE: Record<CheckpointModeType, string> = {
  [CheckpointModeType.DropOff]: 'DO',
  [CheckpointModeType.PickUp]: 'PU',
  [CheckpointModeType.Wait]: 'WT',
};

export const GROUND_TRIP_STATUSES: Record<GroundTripStatus, string> = {
  [GroundTripStatus.New]: 'New',
  [GroundTripStatus.Pending]: 'Pending',
  [GroundTripStatus.Planned]: 'Planned',
  [GroundTripStatus.InProgress]: 'In progress',
  [GroundTripStatus.Completed]: 'Completed',
  [GroundTripStatus.Cancelled]: 'Cancelled',
  [GroundTripStatus.Merged]: 'Merged',
};

export const GROUND_TRIP_ASSIGNMENT_STATUSES: Record<DriverAssignmentStatus | AoAssignmentStatus, string> = {
  [DriverAssignmentStatus.WaitingForAoApproval]: 'Waiting for AO Approval',
  [DriverAssignmentStatus.PendingDriverAssignment]: 'Pending Driver Assignment',
  [DriverAssignmentStatus.PendingDriverApproval]: 'Pending Driver Approval',
  [DriverAssignmentStatus.DriverApproved]: 'Accepted',
  [DriverAssignmentStatus.DriverRejected]: 'Rejected',
  [AoAssignmentStatus.NoAoReview]: 'No Review',
  [AoAssignmentStatus.PendingAoApproval]: 'Pending Approval',
  [AoAssignmentStatus.AoApproved]: 'Accepted',
  [AoAssignmentStatus.AoRejected]: 'Rejected',
};

export const ACTIVE_GROUND_TRIP_STATUSES = [
  GroundTripStatus.InProgress,
  GroundTripStatus.Completed,
  GroundTripStatus.Cancelled,
];

export const DEFAULT_AIRPORTS_QUERY = { withoutFbo: false };
export const CUSTOM_AIRPORTS_QUERY = { withoutFbo: true };

export const SUGGESTED_ROUTE_TYPES_WITH_SURGEON = [RouteTypeSettingsEnum.Full, RouteTypeSettingsEnum.Hub_DH];

export const PARTIAL_SUGGESTED_ROUTE_TYPES = [
  RouteTypeSettingsEnum.Custom_DH_TXC_Hub,
  RouteTypeSettingsEnum.Custom_TXC_Hub,
];
